import { Box, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { ContentBox } from '@/components/shared/content-box';
import { type ResourceApproval } from '@/data/subsidy/interfaces';
import { RefundStatus } from '@/models/subsidy/status';
import { StatusChip } from '@/packages/subsidy/components/status-chip';
import { RefundQueryParams } from '@/packages/subsidy/pages/refund-request/steps/resource-description/interfaces';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';

interface CardProps {
  resourceApproval: ResourceApproval;
}

interface InfoItemProps {
  label: string;
  value: string;
  status?: RefundStatus;
}

const refundableAmount = (resourceApproval: ResourceApproval) => {
  if (resourceApproval.refund_request) {
    return resourceApproval.refund_request.refund
      ? resourceApproval.refund_request.refund.paid_amount
      : resourceApproval.refund_request.refundable_amount;
  }

  return resourceApproval.requested_refundable_amount;
};

const InfoItem = ({ label, value, status }: InfoItemProps) => {
  return (
    <Box minWidth={200}>
      <Typography variant="body2" fontWeight={fontWeights.fontWeightSemibold}>
        {label}
      </Typography>
      {status && <StatusChip status={status} />}
      {!status && <Typography variant="body2">{value}</Typography>}
    </Box>
  );
};

export const RefundCard = ({ resourceApproval }: CardProps) => {
  const navigate = useNavigate();
  const status = resourceApproval.status || 'default';
  const refundRequestUuid = resourceApproval.refund_request?.uuid;
  const revision =
    status === RefundStatus.ResourceApprovalRevisionRequired ||
    status === RefundStatus.RefundRequestRevisionRequired;

  const docsPending =
    status === RefundStatus.ResourceApprovalApproved &&
    !resourceApproval.refund_request;

  return (
    <ContentBox>
      <Typography variant="body2">{resourceApproval?.type?.value}</Typography>
      <Typography variant="h3">{resourceApproval?.name}</Typography>
      <Stack
        direction={{ md: 'row' }}
        alignItems={{ md: 'center' }}
        justifyContent="space-between"
        gap={2}
        mt={2}
      >
        <Stack
          direction={{ md: 'row' }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
          gap={{ md: 4, xs: 2 }}
        >
          <InfoItem
            label="Data da solicitação"
            value={formatDate(resourceApproval.requested_at)}
          />
          <InfoItem
            label="Reembolso solicitado"
            value={formatMoney(refundableAmount(resourceApproval))}
          />
          <InfoItem label="Situação" value={status} status={status} />
        </Stack>
        <Button
          sx={{ width: 'fit-content' }}
          onClick={() =>
            navigate(
              `solicitacao/${resourceApproval.uuid}${refundRequestUuid ? `/pedido/${refundRequestUuid}` : ''}${docsPending ? `?step=${RefundQueryParams.Invoice}` : ''}`
            )
          }
          variant={docsPending || revision ? 'contained' : 'text'}
        >
          {docsPending
            ? 'Enviar comprovantes'
            : revision
              ? 'Corrigir'
              : 'Ver detalhes'}
        </Button>
      </Stack>
    </ContentBox>
  );
};
